import { useState, useEffect } from "react";
import {
  LegacyCard,
  ResourceList,
  ResourceItem,
  Avatar,
  Text,
} from "@shopify/polaris";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { uploadToDokobit } from "services/upload-file.service";
import { signerEditErrorKey } from "utils/signer-edit-errors";
import { useParams } from "react-router-dom";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchContacts } from "services/customers.service";
import { fetch } from "services/user-select.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";

interface ConfirmModalProps {
  fileId: string;
  contactSigners: string[];
  companyUserSigner: string;
  customerId: string;
  nontechuser: string;
  manager: string;
  signType: string;
  subject: string;
  // Runs after the request was sent, e.g. so a list outside redux can refresh.
  onSuccess?: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  fileId,
  contactSigners,
  companyUserSigner,
  customerId,
  nontechuser,
  manager,
  signType,
  subject,
  onSuccess,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, orderId } = useParams();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedCustomerContacts, setSelectedCustomerContacts] = useState<
    any[]
  >([]);
  const [selectedUser, setSelectedUser] = useState<any>("");
  const [selectedNontechUser, setSelectedNontechUser] = useState<any>("");
  const [selectManagerUser, setSelectedManagerUser] = useState<any>("");

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        // Authorized + active contacts only — a signer disabled since the
        // original request must not be re-shown, or re-sent to below.
        const data = await fetchContacts(companyId, customerId);
        const matchedContacts = data.contactsCollection.filter(
          (contact: any) => contactSigners.includes(contact.id)
        );

        setSelectedCustomerContacts(
          matchedContacts.map((contact: any) => ({
            ...contact,
            type: t("customer_contact"),
          }))
        );
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId, customerId]);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const data = await fetch(companyId, {}, false);
        const matchedUser = data.usersCollection.find(
          (user: any) => user.id === companyUserSigner
        );
        const matchedUser_ = data.usersCollection.find(
          (user: any) => user.id === nontechuser
        );
        if (matchedUser)
          setSelectedUser({ ...matchedUser, type: t("sign_permission_user") });
        if (matchedUser_)
          setSelectedNontechUser({
            ...matchedUser_,
            type: t("contact_user"),
          });

        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId]);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const data = await fetch(companyId, {});
        const matchedUser = data.usersCollection.find(
          (user: any) => user.id === manager
        );
        if (matchedUser)
          setSelectedManagerUser({ ...matchedUser, type: t("manager") });
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId]);

  const handleToSendRequest = async () => {
    try {
      setIsLoading(true);
      // Send exactly the contacts shown above, not the raw (possibly stale)
      // signersList ids — a contact removed/disabled since must not be resent.
      const payload = {
        fileId,
        contactSigners: Array.from(
          new Set(selectedCustomerContacts.map((contact: any) => contact.id))
        ),
        companyUserSigner,
        nontechuser,
        manager,
        signType,
        subject,
      };
      await uploadToDokobit(companyId, payload);
      if (fileId && companyId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
      }
      if (customerId)
        dispatch(fetchCustomer({ customerId, companyId: companyId! }));
      if (companyId && orderId)
        dispatch(fetchOrderDetails({ companyId, orderId }));
      onSuccess?.();
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e: any) {
      setErrMsg(signerEditErrorKey(e, "error"));
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_send_document_signature_request")}
      </h1>

      <div style={{ margin: "10px 0" }}>
        <LegacyCard>
          <ResourceList
            resourceName={{ singular: "contact", plural: "contacts" }}
            items={[
              ...selectedCustomerContacts.map((contact) => ({
                id: contact.id,
                name: contact.name,
                email: contact.email,
                type: contact.type,
              })),
              selectedUser && {
                id: selectedUser.id,
                name: selectedUser.name,
                email: selectedUser.email,
                type: selectedUser.type,
              },
              selectedNontechUser && {
                id: selectedNontechUser.id,
                name: selectedNontechUser.name,
                email: selectedNontechUser.email,
                type: selectedNontechUser.type,
              },
              selectManagerUser && {
                id: selectManagerUser.id,
                name: selectManagerUser.name,
                email: selectManagerUser.email,
                type: selectManagerUser.type,
              },
            ].filter(Boolean)}
            renderItem={(item) => (
              <ResourceItem
                id={item.id}
                media={<Avatar customer name={item.name} />}
                accessibilityLabel={`View details for ${item.name}`}
                onClick={() => ""}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div style={{ overflowWrap: "anywhere" }}>
                    <Text as="h3" variant="headingMd">
                      {item.name}
                    </Text>
                    <Text as="h3">{item.email}</Text>
                  </div>
                  <div>
                    <Text as="h3" variant="bodySm">
                      {item.type}
                    </Text>
                  </div>
                </div>
              </ResourceItem>
            )}
          />
        </LegacyCard>
      </div>

      <InlineStack align="space-between">
        <Button onClick={handleToSendRequest} loading={isLoading}>
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
